import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Screen } from '@/components/Screen'
import { InsightWithEvidence } from '@/components/InsightWithEvidence'
import { validAggregate, type Aggregate } from '@/lib/aggregate'
import { Kicker } from '@/components/Kicker'
import { Button } from '@/components/Button'
import { Field, TextArea } from '@/components/Field'
import { ConfidenceChip } from '@/components/ConfidenceChip'
import { useAppStore } from '@/stores/appStore'
import { supabase } from '@/lib/supabase'
import { PACK_COLORS } from '@/design-system/tokens'
import { SparkleIcon } from '@/design-system/icons'
import { useFocusedPerson } from '@/hooks/useFocusedPerson'

type VoiceState = 'idle' | 'requesting' | 'recording' | 'denied' | 'unsupported' | 'captured'

/**
 * AskScreen — Ask Wren.
 * Calls the `wren-brain` Edge Function (task='chat') which returns
 * { text, sources } grounded in the focused person's snapshot.
 */

interface AskResponse {
  answer: string
  confidence: number | null
  citation: string | null
  computed_facts: Record<string, Aggregate>
  scope: string
}

const SUGGESTIONS = [
  'When was the last feed?',
  'How is feeding trending this week?',
  'What open needs does Baby have?',
  'How much sleep has she gotten today?',
  'Any medications given today?',
]

export function AskScreen() {
  const familyId = useAppStore((s) => s.familyId)
  const userId = useAppStore((s) => s.user?.id)
  const person = useFocusedPerson()
  const [params, setParams] = useSearchParams()
  const [question, setQuestion] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [answer, setAnswer] = useState<AskResponse | null>(null)
  const [voice, setVoice] = useState<VoiceState>('idle')
  const recorderRef = useRef<MediaRecorder | null>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const packColor = PACK_COLORS.needs.light

  const wantsVoice = params.get('voice') === '1'

  useEffect(() => {
    if (!wantsVoice || voice !== 'idle') return
    const supported = typeof navigator !== 'undefined'
      && !!navigator.mediaDevices?.getUserMedia
      && typeof window !== 'undefined'
      && typeof window.MediaRecorder === 'function'
    if (!supported) { setVoice('unsupported'); return }
    setVoice('requesting')
    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream) => {
        streamRef.current = stream
        const rec = new MediaRecorder(stream)
        recorderRef.current = rec
        rec.start()
        setVoice('recording')
      })
      .catch(() => { setVoice('denied') })
    return () => {
      recorderRef.current?.state === 'recording' && recorderRef.current.stop()
      streamRef.current?.getTracks().forEach((t) => t.stop())
    }
  }, [wantsVoice, voice])

  function stopRecording() {
    if (recorderRef.current?.state === 'recording') recorderRef.current.stop()
    streamRef.current?.getTracks().forEach((t) => t.stop())
    setVoice('captured')
    const next = new URLSearchParams(params)
    next.delete('voice')
    setParams(next, { replace: true })
  }

  async function ask(qOverride?: string) {
    const q = (qOverride ?? question).trim()
    if (!q || !familyId) return
    if (!person?.id) { setErr('Select a person to ask about first.'); return }
    setBusy(true); setErr(null); setAnswer(null)
    try {
      const { data, error } = await supabase.functions.invoke('wren-brain', {
        body: { task: 'chat', person_id: person.id, user_message: q },
      })
      if (error) throw new Error(error.message)
      if (!data || typeof data !== 'object') throw new Error('AI returned no answer')
      const brain = data as { text?: string | null; snapshot?: Record<string, unknown> }
      if (!brain.snapshot || !Object.values(brain.snapshot).every(validAggregate)) throw new Error('Unverified aggregate response; no numbers shown.')
      setAnswer({ answer: brain.text ?? 'Inspect the verified totals below; a fuller interpretation is unavailable.', confidence: null, citation: null,
        computed_facts: brain.snapshot as Record<string, Aggregate>, scope: JSON.stringify([familyId, userId, person.id]) })
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e))
    } finally {
      setBusy(false)
    }
  }

  return (
    <Screen title="Ask Wren">
      <div className="px-5 py-5 space-y-5">
        <div
          className="rounded-r-sm p-4"
          style={{ background: `linear-gradient(180deg, ${packColor}10, transparent), #141B19`, borderTop: `1px solid ${packColor}55` }}
        >
          <div className="flex items-center gap-2">
            <SparkleIcon size={18} style={{ color: packColor }} />
            <Kicker color={packColor}>Grounded in your data</Kicker>
          </div>
          <p className="mt-2 text-caption text-text-secondary">
            Wren answers questions by querying your family's actual records — never guessing. If it can't find the answer with high confidence,
            it will say so.
          </p>
        </div>

        {voice !== 'idle' && (
          <div
            className="rounded-r-sm p-4 flex items-center gap-3"
            style={{ background: voice === 'recording' ? `${packColor}18` : '#141B19', border: `1px solid ${packColor}55` }}
          >
            <div
              className={`h-3 w-3 rounded-full ${voice === 'recording' ? 'animate-pulse' : ''}`}
              style={{ background: voice === 'recording' ? '#F26A1F' : packColor }}
              aria-hidden
            />
            <div className="flex-1 text-body text-text-primary">
              {voice === 'requesting' && 'Requesting microphone access…'}
              {voice === 'recording' && 'Listening — tap Stop when done.'}
              {voice === 'denied' && 'Microphone denied. Type your question below.'}
              {voice === 'unsupported' && 'Voice not supported on this device. Type below.'}
              {voice === 'captured' && 'Voice captured — transcription lands in Phase G. Type below for now.'}
            </div>
            {voice === 'recording' && (
              <button
                type="button"
                onClick={stopRecording}
                className="rounded-full px-3 py-1 text-caption font-medium text-inverted"
                style={{ background: '#F26A1F', color: 'white' }}
              >
                Stop
              </button>
            )}
          </div>
        )}

        <Field label="Question">
          <TextArea
            rows={3}
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder="Ask anything about your family's records…"
          />
        </Field>

        <Button
          variant="primary"
          size="lg"
          packColor={packColor}
          onClick={() => ask()}
          loading={busy}
          disabled={!question.trim()}
          className="w-full"
        >
          Ask
        </Button>

        <div>
          <Kicker>Try</Kicker>
          <div className="mt-2 flex flex-wrap gap-2">
            {SUGGESTIONS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => { setQuestion(s); void ask(s) }}
                className="inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-caption border border-divider-subtle text-text-secondary hover:bg-white/5"
              >
                {s}
              </button>
            ))}
          </div>
        </div>

        {err && (
          <div className="rounded-r-sm bg-pack-safety/15 border border-pack-safety/30 p-3 text-caption text-pack-safety">{err}</div>
        )}

        {answer && answer.scope === JSON.stringify([familyId, userId, person?.id]) && (
          <section className="rounded-r-sm bg-bg-elevated border border-divider-subtle p-4 space-y-3">
            <div className="flex items-center gap-2">
              <Kicker color={packColor}>Answer</Kicker>
              {answer.confidence != null && <ConfidenceChip confidence={answer.confidence} />}
            </div>
            <div className="text-body text-text-primary whitespace-pre-wrap">{answer.answer}</div>
            {answer.citation && (
              <div className="pt-2 border-t border-divider-subtle">
                <Kicker>Citation</Kicker>
                <div className="mt-1 text-caption text-text-tertiary font-mono">{answer.citation}</div>
              </div>
            )}
            {(() => {
              const facts = Object.entries(answer.computed_facts ?? {})
              const known = facts.filter(([, a]) => a.status === 'known')
              if (known.length === 0) return null // Suppress the "unknown wall" entirely when there's nothing to show.
              return (
                <div className="pt-2 border-t border-divider-subtle">
                  <Kicker>Recorded observations</Kicker>
                  <div className="mt-2 space-y-2">
                    {known.map(([key, a]) =>
                      a.status === 'known'
                        ? <InsightWithEvidence key={key} number={a.number} unit={a.unit} label={a.label} evidence={a.evidence} />
                        : null,
                    )}
                  </div>
                </div>
              )
            })()}
          </section>
        )}
      </div>
    </Screen>
  )
}
